import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientiApi, ficApi, getApiBase } from '../services/api';

export default function ClientiPage() {
  const [clientiDb, setClientiDb]   = useState([]);
  const [clientiFic, setClientiFic] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [q, setQ]                   = useState('');
  const [filtroZona, setFiltroZona]       = useState('');
  const [filtroImpianto, setFiltroImpianto] = useState('');
  const [fonte, setFonte]           = useState('tutti'); // tutti | db | fic
  const [filtroStato, setFiltroStato] = useState('attivi'); // attivi | sospesi | tutti

  // Mesi di manutenzione: "MARZO" + "SETTEMBRE" -> "Marzo/Settembre"
  const fmtMese = m => {
    if (!m) return null;
    const s = String(m).trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : null;
  };
  const MESI_ORD = ['gennaio','febbraio','marzo','aprile','maggio','giugno',
                    'luglio','agosto','settembre','ottobre','novembre','dicembre'];

  const BadgeMese = ({ nome }) => {
    const i = MESI_ORD.indexOf(nome.toLowerCase());
    const h = i >= 0 ? i * 30 : 0;               // una tonalità ogni 30°
    return (
      <span style={{
        display:'inline-block', padding:'2px 7px', borderRadius:10, fontSize:11,
        fontWeight:600, whiteSpace:'nowrap',
        background: i >= 0 ? `hsl(${h}, 72%, 90%)` : '#f1f5f9',
        color:      i >= 0 ? `hsl(${h}, 65%, 28%)` : '#64748b',
      }}>{nome}</span>
    );
  };

  const manutenzioni = c => {
    const mesi = [fmtMese(c.mese_manutenzione_1), fmtMese(c.mese_manutenzione_2)].filter(Boolean);
    if (!mesi.length) return <span style={{ color:'#9ca3af' }}>—</span>;
    return (
      <span style={{ display:'inline-flex', gap:4 }}>
        {mesi.map((m, k) => <BadgeMese key={k} nome={m} />)}
      </span>
    );
  };
  const [importando, setImportando] = useState(null);
  const navigate = useNavigate();

  const carica = React.useCallback(() => {
    setLoading(true);
    Promise.all([clientiApi.getAll(), ficApi.clienti()])
      .then(([dbRes, ficRes]) => {
        setClientiDb(dbRes.data?.data || []);
        const ficData = ficRes.data?.data || [];
        setClientiFic(ficData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const importaClienteFic = async (f) => {
    setImportando(f.id);
    try {
      const token = localStorage.getItem('quindi_token');
      const res = await fetch(`${getApiBase()}/api/fic/importa-cliente/${f.id}`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
      });
      const j = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(j.error || 'Errore importazione');
      if (j.giaEsistente) {
        alert('Questo cliente è già presente nel DB locale.');
      } else {
        alert(`✅ Cliente "${f.name}" scaricato nel DB locale.`);
      }
      carica(); // ricarica le liste
    } catch(e) {
      alert('Errore: ' + e.message);
    } finally { setImportando(null); }
  };

  // Indice clienti DB mappati su FIC
  const ficIdsMappati = new Set(clientiDb.filter(c => c.ficClienteId || c.fic_cliente_id)
    .map(c => String(c.ficClienteId || c.fic_cliente_id)));

  const zone = [...new Set(clientiDb.map(c => c.zona).filter(Boolean))].sort();

  // Clienti DB filtrati
  const isSospeso = c => c.sospeso === 1 || c.sospeso === true || c.sospeso === '1';
  const dbFiltrati = clientiDb.filter(c => {
    // Filtro stato attivi/sospesi
    const susp = isSospeso(c);
    if (filtroStato === 'attivi'  && susp) return false;
    if (filtroStato === 'sospesi' && !susp) return false;
    const matchZ = !filtroZona || c.zona === filtroZona;
    const matchI = !filtroImpianto || (() => {
      const imp = filtroImpianto;
      if (imp === 'gruppi_pompa')     return c.gruppi_pompa > 0     || c.n_gruppi_pompa > 0;
      if (imp === 'rivelazione_fumi') return c.rivelazione_fumi > 0  || c.n_fumi > 0;
      if (imp === 'sprinkler')        return c.sprinkler > 0;
      if (imp === 'idranti')          return c.idranti > 0;
      if (imp === 'porte')            return c.porte > 0;
      if (imp === 'estintori')        return c.estintori > 0;
      return true;
    })();
    if (!q) return matchZ && matchI;
    const parole = q.toLowerCase().split(' ').filter(Boolean);
    const campi  = [c.nome, c.indirizzo, c.citta, c.zona, c.cap].filter(Boolean).join(' ').toLowerCase();
    return matchZ && matchI && parole.every(p => campi.includes(p));
  });

  // Clienti solo FIC (non nel DB)
  const ficSolo = clientiFic.filter(f => !ficIdsMappati.has(String(f.id)));
  const ficFiltrati = ficSolo.filter(f =>
    !q || f.name?.toLowerCase().includes(q.toLowerCase())
  );

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="page-content">
      {/* Contatori */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card blue" onClick={() => setFonte('tutti')} style={{ cursor:'pointer' }}>
          <div className="stat-value">{clientiDb.length}</div>
          <div className="stat-label">🗄️ DB Locale</div>
        </div>
        <div className="stat-card green" onClick={() => setFonte('mappati')} style={{ cursor:'pointer' }}>
          <div className="stat-value">{clientiDb.filter(c=>c.ficClienteId||c.fic_cliente_id).length}</div>
          <div className="stat-label">✅ Collegati FIC</div>
        </div>
        <div className="stat-card red" onClick={() => setFonte('non_mappati')} style={{ cursor:'pointer' }}>
          <div className="stat-value">{clientiDb.filter(c=>!c.ficClienteId&&!c.fic_cliente_id).length}</div>
          <div className="stat-label">❌ Non collegati</div>
        </div>
        <div className="stat-card" style={{ borderLeft:'3px solid #0d6efd', cursor:'pointer' }} onClick={() => setFonte('fic')}>
          <div className="stat-value" style={{ color:'#0d6efd' }}>{ficSolo.length}</div>
          <div className="stat-label">☁️ Solo FIC</div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <input className="form-control" style={{ maxWidth:260 }}
          placeholder="🔍 Cerca cliente..."
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-control" style={{ maxWidth:200 }}
          value={filtroZona} onChange={e => setFiltroZona(e.target.value)}>
          <option value="">Tutte le zone</option>
          {zone.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <select className="form-control" style={{ maxWidth:220 }}
          value={filtroImpianto} onChange={e => setFiltroImpianto(e.target.value)}>
          <option value="">Tutti gli impianti</option>
          <option value="estintori">Con estintori</option>
          <option value="idranti">Con idranti/naspi</option>
          <option value="porte">Con porte REI</option>
          <option value="rivelazione_fumi">Con rilevazione fumi</option>
          <option value="sprinkler">Con sprinkler</option>
          <option value="gruppi_pompa">Con gruppi pompa</option>
        </select>
        <select className="form-control" style={{ maxWidth:180 }}
          value={fonte} onChange={e => setFonte(e.target.value)}>
          <option value="tutti">Tutti</option>
          <option value="db">Solo DB locale</option>
          <option value="fic">Solo FIC</option>
          <option value="mappati">Collegati FIC</option>
          <option value="non_mappati">Non collegati</option>
        </select>
        <select className="form-control" style={{ maxWidth:170 }}
          value={filtroStato} onChange={e => setFiltroStato(e.target.value)}>
          <option value="attivi">✅ Solo attivi</option>
          <option value="sospesi">❄️ Solo sospesi</option>
          <option value="tutti">Tutti (attivi+sospesi)</option>
        </select>
        <button className="btn btn-primary" onClick={() => navigate('/clienti/nuovo')}>
          ➕ Nuovo
        </button>
      </div>

      {/* Tabella DB */}
      {fonte !== 'fic' && (
        <div className="card" style={{ padding:0, marginBottom:16 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb', fontWeight:600, fontSize:13, color:'#6b7280' }}>
            🗄️ Clienti DB locale ({dbFiltrati.length})
          </div>
          <div className="table-wrap">
            <table className="q-table">
              <thead>
                <tr><th>Cliente</th><th>Indirizzo</th><th>Città</th><th>Zona</th><th>Manutenzioni</th><th>FIC</th><th></th></tr>
              </thead>
              <tbody>
                {dbFiltrati.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign:'center', color:'#9ca3af', padding:24 }}>Nessun cliente</td></tr>
                )}
                {dbFiltrati
                  .filter(c => {
                    if (fonte === 'mappati')    return c.ficClienteId || c.fic_cliente_id;
                    if (fonte === 'non_mappati')return !c.ficClienteId && !c.fic_cliente_id;
                    return true;
                  })
                  .map(c => (
                  <tr key={c.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/clienti/${c.id}`)}>
                    <td>
                      <strong>{c.nome}</strong>
                      {(c.sospeso==1||c.sospeso===true) && <span className="badge" style={{ marginLeft:6, background:'#e0f2fe', color:'#0369a1' }}>❄️ Congelato</span>}
                    </td>
                    <td style={{ fontSize:12, color:'#6b7280' }}>{c.indirizzo||'—'}</td>
                    <td style={{ fontSize:12, color:'#6b7280' }}>{c.citta||'—'}</td>
                    <td>{c.zona && <span className="badge badge-gray">{c.zona}</span>}</td>
                    <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{manutenzioni(c)}</td>
                    <td>
                      {(c.ficClienteId||c.fic_cliente_id)
                        ? <span className="badge badge-green">✅ FIC</span>
                        : <span className="badge badge-gray">—</span>}
                    </td>
                    <td><button className="btn btn-secondary btn-sm"
                      onClick={e=>{e.stopPropagation();navigate(`/clienti/${c.id}`);}}>Apri →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabella FIC solo */}
      {(fonte === 'tutti' || fonte === 'fic') && ficFiltrati.length > 0 && (
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb', fontWeight:600, fontSize:13, color:'#084298', background:'#f0f7ff' }}>
            ☁️ Solo su FIC ({ficFiltrati.length})
          </div>
          <div className="table-wrap">
            <table className="q-table">
              <thead>
                <tr><th>Nome</th><th>Città</th><th>P.IVA</th><th>Email</th><th></th></tr>
              </thead>
              <tbody>
                {ficFiltrati.map(f => (
                  <tr key={f.id} style={{ background:'#f8fbff' }}>
                    <td><strong>{f.name}</strong></td>
                    <td style={{ fontSize:12, color:'#6b7280' }}>{f.address_city||f.city||'—'}</td>
                    <td style={{ fontSize:12, color:'#6b7280' }}>{f.vat_number||'—'}</td>
                    <td style={{ fontSize:12, color:'#6b7280' }}>{f.email||'—'}</td>
                    <td style={{ textAlign:'right', whiteSpace:'nowrap' }}>
                      <button className="btn btn-primary btn-sm" disabled={importando===f.id}
                        onClick={()=>importaClienteFic(f)}>
                        {importando===f.id ? '...' : '⬇ Scarica in locale'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
